import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { moduleIcon, moduleName } from "@/lib/modules";
import { Sparkles } from "lucide-react";
import { IntelligencePanel } from "@/components/IntelligencePanel";
import { AutomationPanel } from "@/components/AutomationPanel";
import { SupportPanel } from "@/components/SupportPanel";
import { InventoryPanel } from "@/components/InventoryPanel";
import { IntelligenceAnalystPanel } from "@/components/IntelligenceAnalystPanel";
import { CountryPricingPanel } from "@/components/CountryPricingPanel";
import { ReviewsAdminPanel } from "@/components/ReviewsAdminPanel";
import { LegalPanel } from "@/components/LegalPanel";
import { DesignerPanel } from "@/components/DesignerPanel";
import { LearningPanel } from "@/components/LearningPanel";
import CuratedPage from "@/pages/Curated";

export default function ModulePage() {
  const { moduleId } = useParams();
  const { lang } = useLang();
  const [meta, setMeta] = useState(null);
  const [reply, setReply] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);

  const Icon = moduleIcon(moduleId);

  useEffect(() => {
    setMeta(null);
    setReply("");
    setPrompt("");
    api.get(`/modules/${moduleId}`).then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, [moduleId]);

  // Product + Pricing modules share the unified Curated catalog:
  // one page shows AI-curated products with buy price, suggested sell
  // price, trust badges, publish-to-Shopify button, and an inline AI
  // operator chat that can execute orders on the store.
  if (moduleId === "product" || moduleId === "pricing") {
    return <CuratedPage />;
  }

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/briefing", {
        module_id: moduleId,
        language: lang,
        prompt: prompt || null,
      });
      setReply(data.reply);
    } catch {
      setReply("Unable to reach the AI right now.");
    } finally {
      setBusy(false);
    }
  };

  const statusKey = meta?.status ? `module_status_${meta.status}` : null;

  return (
    <div className="space-y-6" data-testid={`module-page-${moduleId}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 border border-[color:var(--hq-cyan)]/40 flex items-center justify-center shrink-0">
          <Icon size={20} className="text-[color:var(--hq-cyan)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            {meta?.role || "module"}
          </div>
          <h1 className="font-display font-light text-3xl tracking-tight mt-1">
            {moduleName(moduleId, lang)}
          </h1>
          {meta && (
            <div className="mt-3 flex items-center gap-2">
              <span
                className={`hq-dot ${
                  meta.status === "active"
                    ? "hq-dot-green"
                    : meta.status === "monitoring"
                    ? "hq-dot-cyan"
                    : "hq-dot-yellow"
                }`}
              ></span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                {statusKey ? t(lang, statusKey) : meta.status}
              </span>
            </div>
          )}
        </div>
      </div>

      {meta?.description && (
        <div className="hq-card p-6">
          <p className="text-white/70 leading-relaxed">{meta.description}</p>
        </div>
      )}

      <div className="hq-card p-6 border-l-2 border-[color:var(--hq-cyan)]" data-testid="module-howto">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-[color:var(--hq-cyan)]" />
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--hq-cyan)]">
            {t(lang, "module_howto_title")}
          </div>
        </div>
        <p className="text-white/80 leading-relaxed text-sm whitespace-pre-line">
          {t(lang, `howto_${moduleId}`)}
        </p>
      </div>

      <div className="hq-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={14} className="text-[color:var(--hq-cyan)]" />
          <div className="font-display text-lg">{t(lang, "module_briefing_title")}</div>
          <span className="ml-auto font-mono text-[10px] uppercase text-white/40">GPT-5.2</span>
        </div>

        <textarea
          data-testid="module-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="hq-input min-h-[80px] resize-y"
          placeholder={t(lang, "module_briefing_placeholder")}
        />

        <div className="mt-3">
          <button
            data-testid="module-generate-btn"
            onClick={generate}
            disabled={busy}
            className="hq-btn hq-btn-primary disabled:opacity-50"
          >
            {busy ? t(lang, "module_briefing_generating") : t(lang, "module_briefing_generate")}
          </button>
        </div>

        <div
          data-testid="module-briefing-content"
          className="mt-6 min-h-[80px] text-white/80 leading-relaxed whitespace-pre-wrap text-sm"
        >
          {busy && !reply ? (
            <span className="text-white/40">…</span>
          ) : (
            reply || <span className="text-white/40">—</span>
          )}
        </div>
      </div>

      {/* Pricing module now renders CuratedPage via the early-return at the top of this component */}
      {moduleId === "pricing_disabled_legacy" && (
        <>
          <IntelligencePanel mode="pricing" />
          <CountryPricingPanel />
        </>
      )}
      {moduleId === "customer" && (
        <>
          <SupportPanel />
          <ReviewsAdminPanel />
        </>
      )}
      {moduleId === "supplier" && <InventoryPanel />}
      {moduleId === "trend" && <IntelligenceAnalystPanel mode="trend" />}
      {moduleId === "market" && <IntelligenceAnalystPanel mode="market" />}
      {moduleId === "seo" && <IntelligenceAnalystPanel mode="seo" />}
      {moduleId === "marketing" && (
        <>
          <IntelligenceAnalystPanel mode="marketing" />
          <DesignerPanel />
        </>
      )}
      {moduleId === "guardian" && <LegalPanel />}
      {moduleId === "learning" && <LearningPanel />}
    </div>
  );
}
